import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useSocket } from '../socket/SocketContext';
import { SOUND_MAP } from '../assets/soundMap';
import { isAppSoundEnabled, subscribeToAppSoundPreference } from '../utils/appSound';
import { useStatus } from '../popup/NotificationContext';
import { isDemoReadOnlyUser } from '../utils/demoUser';

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  'https://app.krush.in/api';

function normalizeTaskNotification(item = {}) {
  const id =
    item._id ||
    item.notificationId ||
    item.taskNotificationId ||
    item?.data?.notificationId ||
    item?.data?.taskNotificationId;
  return {
    _id: id,
    queueKey: `task-${id || item.createdAt || Date.now()}`,
    title: item.title || 'Notification',
    message: item.message || '',
    createdAt: item.createdAt || new Date().toISOString(),
    popupType: 'task',
    type: item.type || 'task_assigned',
  };
}

function normalizeFleetNotification(item = {}) {
  const id = item._id || item.notificationId || item?.data?.notificationId;
  return {
    _id: id,
    queueKey: `fleet-${id || item.createdAt || Date.now()}`,
    title: item.title || 'Vehicle alert',
    message: item.message || item.body || '',
    createdAt: item.createdAt || new Date().toISOString(),
    popupType: 'fleet',
    type: item.type || item.eventType || 'alert',
  };
}

function normalizeBroadcastMessage(item = {}) {
  const id = item._id || item.notificationId || item.broadcastId || item.messageId;
  return {
    _id: id,
    queueKey: `broadcast-${id}`,
    title: item.title || `Fleet Message from ${item.senderName || 'CRM'}`,
    message: item.message || '',
    createdAt: item.createdAt || new Date().toISOString(),
    popupType: 'broadcast',
    type: 'broadcast',
    senderName: item.senderName || 'CRM',
  };
}

function formatTime(value) {
  if (!value) return 'Just now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Just now';
  return date.toLocaleString('en-IN');
}

function getToastType(notification = {}) {
  const type = String(notification.type || '').toLowerCase();
  if (['task_completed', 'task_verified', 'task_accepted'].includes(type)) return 'success';
  if (['task_rejected'].includes(type)) return 'warning';
  return 'info';
}

const NotificationPopup = () => {
  const { socket, connected, userId } = useSocket();
  const { showStatus } = useStatus();
  const currentUser = useSelector((state) => state.auth.user);
  const isDemoReadOnly = isDemoReadOnlyUser(currentUser);
  const [queue, setQueue] = useState([]);
  const [notificationSoundKey, setNotificationSoundKey] = useState('default');
  const [soundEnabled, setSoundEnabled] = useState(() => isAppSoundEnabled());
  const pendingAckTimeoutsRef = useRef(new Map());

  const getToken = () =>
    localStorage.getItem('token') || localStorage.getItem('authToken');

  const removeByQueueKey = useCallback((queueKey) => {
    setQueue((prev) => prev.filter((item) => item.queueKey !== queueKey));
  }, []);

  const loadNotificationSoundSetting = useCallback(async () => {
    const token = getToken();
    if (!token) return 'default';

    try {
      const response = await axios.get(`${API_BASE}/notificationSetting`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { alarmType: 'notification', active: true },
      });

      const settings = Array.isArray(response?.data?.settings) ? response.data.settings : [];
      const preferred = settings.find((item) => item?.isActive !== false)?.sound || 'default';
      setNotificationSoundKey(preferred);
      return preferred;
    } catch (error) {
      setNotificationSoundKey('default');
      console.error(
        'Error loading notification sound setting:',
        error?.response?.data || error.message
      );
      return 'default';
    }
  }, []);

  useEffect(() => {
    setSoundEnabled(isAppSoundEnabled());
    return subscribeToAppSoundPreference(setSoundEnabled);
  }, []);

  const playNotificationSound = useCallback((soundKey = notificationSoundKey) => {
    if (!soundEnabled || !isAppSoundEnabled() || soundKey === 'mute') return;

    const selectedSound = SOUND_MAP[soundKey];

    if (selectedSound) {
      try {
        const audio = new Audio(selectedSound);
        audio.preload = 'auto';
        audio.volume = 0.8;
        audio.play().catch((err) => {
          console.warn('Notification sound blocked:', err?.message || err);
        });
        return;
      } catch (_) {}
    }

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 720;
      oscillator.type = 'triangle';

      gainNode.gain.setValueAtTime(0.16, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.7);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.7);
    } catch (err) {
      console.error('Sound error:', err);
    }
  }, [notificationSoundKey, soundEnabled]);

  const pushIntoQueue = useCallback((items) => {
    const normalized = (Array.isArray(items) ? items : [items]).filter(Boolean);
    if (normalized.length === 0) return;

    setQueue((prev) => {
      const merged = [...prev];
      normalized.forEach((item) => {
        if (merged.some((entry) => entry.queueKey === item.queueKey)) return;
        if (pendingAckTimeoutsRef.current.has(item.queueKey)) return;
        merged.push(item);
      });
      merged.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      return merged;
    });
  }, []);

  const loadPendingPopups = useCallback(async () => {
    if (isDemoReadOnly) return;
    const token = getToken();
    if (!token) return;

    try {
      const [taskRes, messageRes, fleetRes] = await Promise.all([
        axios.get(`${API_BASE}/notifications/unacknowledged`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE}/messaging/unacknowledged`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE}/notificationswebapp/user`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 50 },
        }),
      ]);

      const taskItems = Array.isArray(taskRes?.data)
        ? taskRes.data.map(normalizeTaskNotification)
        : [];
      const messageItems = Array.isArray(messageRes?.data)
        ? messageRes.data.map(normalizeBroadcastMessage)
        : [];
      const fleetItems = Array.isArray(fleetRes?.data?.notifications)
        ? fleetRes.data.notifications
            .filter((item) => item?.read !== true)
            .map(normalizeFleetNotification)
        : [];

      pushIntoQueue([...taskItems, ...messageItems, ...fleetItems]);
    } catch (error) {
      console.error('Error loading popup notifications:', error?.response?.data || error.message);
    }
  }, [isDemoReadOnly, pushIntoQueue]);

  useEffect(() => {
    if (isDemoReadOnly) return;
    if (connected && userId) {
      loadNotificationSoundSetting();
      loadPendingPopups();
    }
  }, [connected, userId, isDemoReadOnly, loadNotificationSoundSetting, loadPendingPopups]);

  useEffect(() => {
    if (isDemoReadOnly) return undefined;
    if (!socket) return;

    const handleTaskNotification = async (notification) => {
      const soundKey = await loadNotificationSoundSetting();
      playNotificationSound(soundKey);
      pushIntoQueue(
        notification?.popupType === 'fleet' || notification?.eventType
          ? normalizeFleetNotification(notification)
          : normalizeTaskNotification(notification)
      );
    };

    const handleBroadcastMessage = async (notification) => {
      const soundKey = await loadNotificationSoundSetting();
      playNotificationSound(soundKey);
      pushIntoQueue(normalizeBroadcastMessage(notification));
    };

    socket.on('notification', handleTaskNotification);
    // Reminders created from the CRM use this event. Treat it as a fleet
    // notification so it is displayed immediately without waiting for a
    // reconnect/polling cycle.
    socket.on('reminder:due', handleTaskNotification);
    socket.on('broadcast_message', handleBroadcastMessage);

    return () => {
      socket.off('notification', handleTaskNotification);
      socket.off('reminder:due', handleTaskNotification);
      socket.off('broadcast_message', handleBroadcastMessage);
    };
  }, [isDemoReadOnly, loadNotificationSoundSetting, playNotificationSound, pushIntoQueue, socket]);

  const acknowledgeTask = useCallback(
    async (notification) => {
      const token = getToken();
      try {
        await axios.put(
          `${API_BASE}/notifications/${notification._id}/acknowledge`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (socket) socket.emit('notification:acknowledge', notification._id);
      } catch (err) {
        console.error('Error acknowledging task notification:', err);
      } finally {
        removeByQueueKey(notification.queueKey);
        const timeoutId = pendingAckTimeoutsRef.current.get(notification.queueKey);
        if (timeoutId) {
          clearTimeout(timeoutId);
          pendingAckTimeoutsRef.current.delete(notification.queueKey);
        }
      }
    },
    [removeByQueueKey, socket]
  );

  const acknowledgeBroadcast = useCallback(
    async (notification) => {
      const token = getToken();
      try {
        await axios.put(
          `${API_BASE}/messaging/${notification._id}/acknowledge`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error('Error acknowledging broadcast notification:', err);
      } finally {
        removeByQueueKey(notification.queueKey);
        const timeoutId = pendingAckTimeoutsRef.current.get(notification.queueKey);
        if (timeoutId) {
          clearTimeout(timeoutId);
          pendingAckTimeoutsRef.current.delete(notification.queueKey);
        }
      }
    },
    [removeByQueueKey]
  );

  useEffect(() => {
    if (!connected || queue.length === 0) return;

    queue.forEach((notification) => {
      if (pendingAckTimeoutsRef.current.has(notification.queueKey)) return;

      const subtitle =
        notification.popupType === 'broadcast'
          ? notification.senderName
            ? `Sender: ${notification.senderName}`
            : 'Fleet message'
          : `Received ${formatTime(notification.createdAt)}`;

      showStatus(
        getToastType(notification),
        {
          title: notification.title,
          message: `${notification.message}${subtitle ? `\n${subtitle}` : ''}`,
        },
        { duration: 5000 }
      );

      const timeoutId = window.setTimeout(() => {
        if (notification.popupType === 'broadcast') acknowledgeBroadcast(notification);
        // Vehicle alerts and reminders must remain unread until the user opens
        // the Notification Center or marks them as read. Auto-reading them
        // made the sidebar counter drop back to zero after every popup.
        else if (notification.popupType === 'fleet') removeByQueueKey(notification.queueKey);
        else acknowledgeTask(notification);
      }, 5100);

      pendingAckTimeoutsRef.current.set(notification.queueKey, timeoutId);
    });
  }, [acknowledgeBroadcast, acknowledgeTask, connected, queue, removeByQueueKey, showStatus]);

  useEffect(() => () => {
    pendingAckTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    pendingAckTimeoutsRef.current.clear();
  }, []);

  return null;
};

export default NotificationPopup;
